import { useEffect } from 'react'
import { Coverages, SumInsuredLocal } from '../../@types/RunPetisSimulation'
import { ENV } from '../../Environment'
import { useSimulatorStore } from '../../Stores/simulatorStore'
import { useGenerateProposalDocumentsStore } from '../../Stores/useGenerateProposalDocumentsStore'
import { AnimalsSecured } from '../../Types/AnimalStore'
import { t } from '../../i18n'
import { Checkbox } from '../Checkbox'
import Checked from '../Icons/checked'
import { CellWrapper } from './styles'
import { usePrepareAnimalToRecalculate } from './usePrepareAnimalToRecalculate'
import {
  getExcessDeductibleTextToDisplay,
  getSumInsuredTextToDisplay,
} from './utils'

type InfoCoverageProps = {
  isMandatory?: boolean
  isDisabled?: boolean
  coverageId: string
  hasCoverage: Coverages
  animal?: AnimalsSecured
}

export function InfoCoverage({
  isMandatory,
  coverageId,
  hasCoverage,
  isDisabled = false,
  animal,
}: InfoCoverageProps) {
  const { handleUpdateAnimalToRecalculate } = usePrepareAnimalToRecalculate()
  const {
    setIsRecalculate,
    hasUpdateCheckbox,
    setHasUpdateCheckbox,
    hasRecalculate,
  } = useSimulatorStore()
  const { isGenerateProposalDocuments } = useGenerateProposalDocumentsStore()

  const sumInsuredToDisplayed = getSumInsuredTextToDisplay(
    hasCoverage?.sumInsured as SumInsuredLocal,
  )
  const excessDeductibleToDisplayed = getExcessDeductibleTextToDisplay(
    hasCoverage?.excessDeductible as SumInsuredLocal,
  )

  const handleInfoCoveragesUnlimited = () => {
    const accountingValue = hasCoverage.accountingValue || 0
    const accountingVariableDescription =
      hasCoverage.accountingVariableDescription || ''

    return `${t('pages.step2.info.unlimited')}${
      accountingValue > 0
        ? ` (${t(
            'pages.step2.info.max',
          )} ${accountingValue} ${accountingVariableDescription})`
        : ''
    }`
  }

  const handleInfoCoverageLegalDefense = () => {
    const accountingVariableDescription =
      hasCoverage.accountingVariableDescription || ''
    const referencePeriodVariableDescription =
      hasCoverage.referencePeriodVariableDescription || ''

    return `/${accountingVariableDescription}${
      referencePeriodVariableDescription.length > 0
        ? `/${referencePeriodVariableDescription}`
        : ''
    }`
  }

  const handleCheckedChange = (value: boolean) => {
    handleUpdateAnimalToRecalculate({
      animal: animal as never,
      coverageId,
      value,
    })

    setHasUpdateCheckbox(!hasUpdateCheckbox)
  }

  useEffect(() => {
    if (hasRecalculate) {
      const itemsToRecalculate = Object.values(hasRecalculate).flatMap(
        (item) => item,
      )

      if (itemsToRecalculate.length > 0) {
        setIsRecalculate(true)
      } else {
        setIsRecalculate(false)
      }
    }
  }, [hasRecalculate, setIsRecalculate])

  return (
    <CellWrapper>
      {isMandatory ? (
        <Checked color="#333" width={24} height={24} />
      ) : (
        <div className="checkContainer">
          <Checkbox
            checked={hasCoverage?.isPreSelected || false}
            onChange={(value) => handleCheckedChange(value)}
            disabled={isDisabled || isGenerateProposalDocuments}
          />
        </div>
      )}

      {(sumInsuredToDisplayed || '')?.length > 0 && (
        <p className="description no-margin-top">
          {t('pages.step2.table.sumInsured')}
          <span className="bold">
            {sumInsuredToDisplayed}
            {(hasCoverage.accountingValue || 0) > 0 &&
              coverageId === ENV().CoverageId.LegalDefense && (
                <span>{handleInfoCoverageLegalDefense()}</span>
              )}
          </span>
        </p>
      )}
      {(excessDeductibleToDisplayed || '')?.length > 0 && (
        <p className="description">
          {t('pages.step2.table.excessDeductible')}
          <span className="bold">{excessDeductibleToDisplayed}</span>
          <br />
          {coverageId === ENV().CoverageId.Vaccine &&
            t('pages.step2.table.annualyUtilization')}
        </p>
      )}
      {(coverageId === ENV().CoverageId.AnimalCustody ||
        coverageId === ENV().CoverageId.OrganizationFuneralServices ||
        coverageId === ENV().CoverageId.VariousInformation) && (
        <p className="description  no-margin-top">
          {handleInfoCoveragesUnlimited()}
        </p>
      )}
    </CellWrapper>
  )
}
